// WebSocketService (class)
import { io, Socket } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL;

class WebSocketService {
    private static instance: WebSocketService;
    private socket: Socket | null = null;
    private listeners: Map<string, Set<(data: any) => void>> = new Map();
    private currentConversationId: string | null = null;

    private constructor() {
        // Private constructor to enforce singleton pattern
    }

    public static getInstance(): WebSocketService {
        if (!WebSocketService.instance) {
            WebSocketService.instance = new WebSocketService();
        }
        return WebSocketService.instance;
    }

    public connect(options?: { conversationId?: string; token?: string; url?: string }): void {
        const { conversationId, token, url } = options || {};
        this.currentConversationId = conversationId || this.currentConversationId;

        if (!this.socket) {
            this.socket = io(url || `${API_URL}`, {
                path: '/api/v1/chat/web/socket',
                transports: ['websocket'],
                autoConnect: true,
                auth: {
                    token,
                    conversationId: this.currentConversationId || undefined,
                }
            });

            this.socket.on('connect', () => {
                if (this.currentConversationId) {
                    this.socket?.emit('joinConversation', `room_${this.currentConversationId}`);
                }
                this.notifyListeners('connect', { connected: true });
            });

            this.socket.on('disconnect', (reason: any) => {
                this.notifyListeners('disconnect', { connected: false, reason });
            });

            this.socket.on('connect_error', (error: any) => {
                this.notifyListeners('connect_error', error);
            });

            this.socket.on('error', (error: any) => {
                this.notifyListeners('error', error);
            });

            this.socket.on('reconnect', (attemptNumber: number) => {
                if (this.currentConversationId) {
                    this.socket?.emit('joinConversation', `room_${this.currentConversationId}`);
                }
                this.notifyListeners('reconnect', { attemptNumber });
            });

            this.socket.on('message', (data: any) => {
                this.notifyListeners('message', data);
            });

            this.socket.on('agent_assigned', (data: any) => {
                this.notifyListeners('agent_assigned', data);
            });

            this.socket.on('agent_message', (data: any) => {
                this.notifyListeners('agent_message', data);
            });

            this.socket.on('agent_session_closed', (data: any) => {
                this.notifyListeners('agent_session_closed', data);
            });

            this.socket.on('joinConversation', (data: any) => {
                this.notifyListeners('joinConversation', data);
            });

            this.socket.on('new-messages', (data: any) => {
                this.notifyListeners('new-messages', data);
            });
        }
    }

    public joinConversation(conversationId: string): void {
        this.currentConversationId = conversationId;
        if (this.socket && this.socket.connected) {
            this.socket.emit('joinConversation', `room_${conversationId}`);
        } else {
            this.connect({ conversationId });
        }
    }

    public disconnect(): void {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
    }

    public sendMessage(message: any): void {
        if (this.socket && this.socket.connected) {
            this.socket.emit('message', message);
        } else {
            console.error('WebSocket is not connected');
        }
    }

    public subscribe(event: string, callback: (data: any) => void): void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)?.add(callback);
    }

    public unsubscribe(event: string, callback: (data: any) => void): void {
        this.listeners.get(event)?.delete(callback);
    }

    private notifyListeners(event: string, data: any): void {
        this.listeners.get(event)?.forEach(callback => callback(data));
    }

    public isConnected(): boolean {
        return this.socket?.connected || false;
    }

    private emitGetNewerMessagesEvent(conversationId?: string): void {
        const activeConversation = conversationId || this.currentConversationId;
        if (!activeConversation) {
            return;
        }

        if (this.socket && this.socket.connected) {
            const payload: { conversationId: string; } = { 
                conversationId: activeConversation 
            };

            this.socket.emit('get-newer-messages', payload);
        }
    }

    public requestNewerMessages(conversationId?: string): void {
        if (this.socket && !this.socket.connected) {
            // If the socket is disconnected, attempt to reconnect with the provided conversation first.
            this.connect({ conversationId: conversationId || this.currentConversationId || undefined });
            return;
        }

        this.emitGetNewerMessagesEvent(conversationId);
    }
}
export const webSocketService = WebSocketService.getInstance();
